// crewbit-admin/src/components/money/BulkActionMount.tsx
//
// §Money.HardeningBulkPass2 Phase 3 — owns bulk-action local state (kind +
// reason + method) and dispatches to the right bulk RPC wrapper. Extracted
// from Invoices.tsx to keep that composer under Golden Rule #14 (500 lines).
//
// Parent passes:
//   - active action via `action` prop (null when no modal open)
//   - selectedInvoices (the rows to operate on)
//   - onClose callback (parent clears action + selection)
//   - onResult callback (parent toasts success/failure + refreshes list)

import { useState } from 'react';
import { palette, radii } from '../../styles/tokens';
import {
  BulkActionConfirmModal,
  type BulkActionKind,
} from './BulkActionConfirmModal';
import {
  deleteDraftInvoicesBulk,
  cancelInvoicesBulk,
  sendReminderBulk,
  markInvoicesPaidBulk,
  type BulkInvoiceEnvelope,
} from '../../lib/invoiceBulkMutations';
import type { MoneyRpcResult } from '../../lib/moneyRpc';
import type { InvoiceRow } from '../../types';

type PaymentMethod = 'cash' | 'cheque' | 'etransfer' | 'other';

export interface BulkActionMountProps {
  action: BulkActionKind | null;
  selectedInvoices: InvoiceRow[];
  onClose: () => void;
  onResult: (envelope: BulkInvoiceEnvelope) => void;
}

export function BulkActionMount({ action, selectedInvoices, onClose, onResult }: BulkActionMountProps) {
  // Component unmounts when action goes null (early return below) and remounts
  // fresh on next non-null action — so reason/method state resets naturally.
  // Parent must ensure action transitions through null between two non-null
  // values (typical flow: click action → modal opens; modal closes → null;
  // click another action → modal opens fresh).
  const [reason, setReason] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');

  if (!action) return null;

  const perform = async (ids: string[]): Promise<MoneyRpcResult<BulkInvoiceEnvelope>> => {
    switch (action) {
      case 'delete_drafts':
        return deleteDraftInvoicesBulk(ids);
      case 'cancel':
        return cancelInvoicesBulk(ids, reason || 'Cancelled in bulk');
      case 'send_reminder':
        return sendReminderBulk(ids, 'email');
      case 'mark_paid':
        return markInvoicesPaidBulk(ids, method);
    }
  };

  return (
    <BulkActionConfirmModal
      isOpen={true}
      onClose={onClose}
      action={action}
      selectedInvoices={selectedInvoices}
      perform={perform}
      onResult={onResult}
      reasonInput={
        action === 'cancel' ? (
          <label style={{ display: 'block', fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: palette.g600 }}>Reason (optional)</div>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer requested cancellation"
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: radii.sm,
                border: `1px solid ${palette.g200}`,
                fontSize: 13,
                fontFamily: 'inherit',
              }}
            />
          </label>
        ) : undefined
      }
      methodPicker={
        action === 'mark_paid' ? (
          <label style={{ display: 'block', fontSize: 13 }}>
            <div style={{ marginBottom: 4, color: palette.g600 }}>Payment method</div>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: radii.sm,
                border: `1px solid ${palette.g200}`,
                fontSize: 13,
                fontFamily: 'inherit',
                background: palette.white,
              }}
            >
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="etransfer">e-Transfer</option>
              <option value="other">Other</option>
            </select>
          </label>
        ) : undefined
      }
    />
  );
}
